import { motion } from "framer-motion";
import { Camera, Palette, Settings, Scissors, Sparkles, Video } from "lucide-react";

const TEAM = [
    { name: "Zohaib Saeed", role: "Creative Director", photo: "/team/creative_director.webp", icon: Sparkles },
    { name: "Asim", role: "Head of Videography", photo: "/team/asim.webp", icon: Video },
    { name: "Hamza", role: "Head of Photography", photo: "/team/hamza.webp", icon: Camera },
    { name: "Umair", role: "Head of Post-Production", photo: "/team/umair.webp", icon: Scissors },
    { name: "Talha", role: "Head of Creatives", photo: "/team/talha.webp", icon: Palette },
    { name: "Safi", role: "Operations", photo: "/team/safi.webp", icon: Settings },
];

function MemberCard({ member, index }) {
    const Icon = member.icon;
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: index * 0.06, ease: [0.16, 1, 0.3, 1] }}
            className="group relative bg-[#141414] border border-white/10 hover:border-brand/60 transition-colors overflow-hidden"
            data-testid={`team-card-${member.name.toLowerCase().replace(/\s+/g, "-")}`}
        >
            <div className="relative aspect-[3/4] bg-black overflow-hidden">
                <img
                    src={member.photo}
                    alt={member.name}
                    loading="lazy"
                    draggable={false}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-90 group-hover:opacity-60 transition-opacity" />

                <div className="absolute top-3 left-3 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/80">GWB</span>
                </div>

                <div className="absolute top-3 right-3 w-9 h-9 bg-black/50 backdrop-blur border border-white/15 flex items-center justify-center group-hover:bg-brand group-hover:border-brand transition-colors">
                    <Icon className="w-4 h-4 text-white" />
                </div>
            </div>

            <div className="px-5 py-4 border-t border-white/10 bg-[#0d0d0f]">
                <div className="font-display font-semibold text-xl text-white tracking-[-0.02em] truncate">
                    {member.name}
                </div>
                <div
                    className="mt-1.5 text-[10px] font-mono uppercase tracking-widest text-brand truncate"
                    dangerouslySetInnerHTML={{ __html: member.role }}
                />
            </div>
        </motion.div>
    );
}

export default function Team() {
    return (
        <section id="team" className="relative py-24 sm:py-32 border-t border-white/10 bg-[#0d0d0f] overflow-hidden">
            <div className="pointer-events-none absolute -bottom-40 right-0 w-[700px] h-[500px] rounded-full bg-brand/10 blur-[180px]" />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-14">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        className="md:col-span-8"
                    >
                        <div className="eyebrow mb-4 flex items-center gap-3">
                            <span className="inline-block w-10 h-[2px] bg-brand" /> The team behind the brand
                        </div>
                        <h2 className="font-display font-medium tracking-[-0.04em] text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.02]">
                            Small team.<br />
                            <span className="text-brand">Sharp minds.</span>
                        </h2>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.15 }}
                        className="md:col-span-4 md:text-right"
                    >
                        <div className="text-white/60 text-sm leading-relaxed">
                            Six specialists. Every clip, post, ad and system passes
                            through the right hands &mdash; no outsourcing, no guesswork.
                        </div>
                    </motion.div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-5">
                    {TEAM.map((m, i) => (
                        <MemberCard key={m.name} member={m} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}
